import React, { useState, useMemo, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsSankey from 'highcharts/modules/sankey';
import HighchartsExporting from 'highcharts/modules/exporting';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Grid,
    Button,
    Paper // --- Import Paper
} from '@mui/material';
import { styled } from '@mui/material/styles'; // --- Import styled
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { advanceSearch } from "../../../../_helper/services/advanceSearchv2"
import { getFormalApiErrorMessage } from "../../../../utils/apiError"

HighchartsSankey(Highcharts);
HighchartsExporting(Highcharts);

// --- NEW: Define the Item component using styled ---
const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : 'transparent',
    // ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    // color: theme.palette.text.secondary,
    boxShadow: 'none',
}));


const cleanValue = (value) => {
    if (value === null || typeof value === 'undefined' || String(value).trim() === '') {
        return 'Not Specified';
    }
    return String(value).trim();
};

const selectableFields = {
    'Education': 'EDUCATION',
    'Veteran': 'VETERAN',
    'DD214 Submitted': 'DD214',
    'Marital Status': 'MARTIAL',
    'Gender': 'GENDER',
    'Qualify Status': 'QUALIFY_STATUS',
    'Police Clearance': 'POLICE_CLEARANCE',
    'Drivers License': 'DRIVERS_LICENSE',
    'Disability': 'DISABLE',
    'Race': 'RACE',
};

const allPossibleStages = Object.keys(selectableFields);

// --- NEW: Define initial stages for easy reset ---
const initialSelectedStages = ['Education', 'Veteran', 'Qualify Status'];

const JobShankeyChart = () => {

    const [chartData, setChartData] = useState([]);
    const [chartXData, setXChartData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(9999);

    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');


    const getChartData = async () => {

        const conditions = [];

        if (appliedSearchTerm) {
            conditions.push(`(ANNOUNCEMENT_ID LIKE '%${appliedSearchTerm}%' OR application_no LIKE '%${appliedSearchTerm}%' OR pay_grade LIKE '%${appliedSearchTerm}%' OR PositionTitle LIKE '%${appliedSearchTerm}%')`);
        }
        if (fromDate) {
            conditions.push(`APPLICATION_DATE >= '${fromDate}'`);
        }
        if (toDate) {
            conditions.push(`APPLICATION_DATE <= '${toDate}'`);
        }

        const whereCondition = conditions.join(' AND ');

        const payload = {
            tableName: "Sankeyapplicant",
            searchParams: "[]",
            columns: "*",
            location: "pmis-global",
            pageNumber: currentPage,
            pageSize: itemsPerPage,
            sortData: "",
            additionalWhere: whereCondition,
            qID: 0,
        };

        try {
            const response = await advanceSearch(payload);
            const { results, total } = response;
            setChartData(results);
        } catch (error) {
            console.error(getFormalApiErrorMessage(error, "Something went wrong"));
        }
    };

    useEffect(() => {
        getChartData();
    }, [currentPage, itemsPerPage, appliedSearchTerm, fromDate, toDate]);
    const getChartDataMultiaxis = async () => {


        const payload = {
            tableName: "vw_GG1_TRAN_Sankey",
            searchParams: "[]",
            columns: "*",
            location: "global",
            pageNumber: currentPage,
            pageSize: itemsPerPage,
            sortData: "",
            additionalWhere: '',
            qID: 0,
        };

        try {
            const response = await advanceSearch(payload);
            const { results, total } = response;
            setXChartData(results);
        } catch (error) {
            console.error(getFormalApiErrorMessage(error, "Something went wrong"));
        }
    };

    // useEffect(() => {
    //     getChartDataMultiaxis();
    // }, [currentPage, itemsPerPage]);

    const [selectedStages, setSelectedStages] = useState(initialSelectedStages);

    const handleSearch = () => {
        setAppliedSearchTerm(searchTerm);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setAppliedSearchTerm('');
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    // --- NEW: Handler to reset all filters ---
    const handleReset = () => {
        setSearchTerm('');
        setAppliedSearchTerm('');
        setFromDate('');
        setToDate('');
        setSelectedStages(initialSelectedStages);
    };


    const chartOptions = useMemo(() => {
        if (selectedStages.length < 2) {
            return {
                title: { text: 'Applicant Flow Analysis' },
                subtitle: { text: 'Please select at least two stages to visualize the flow.' },
                series: []
            };
        }

        const links = {};

        chartData?.forEach(application => {
            for (let i = 0; i < selectedStages.length - 1; i++) {
                const fromField = selectedStages[i];
                const toField = selectedStages[i + 1];

                const fromKey = selectableFields[fromField];
                const toKey = selectableFields[toField];

                const fromNode = `${fromField}: ${cleanValue(application[fromKey])}`;
                const toNode = `${toField}: ${cleanValue(application[toKey])}`;

                const linkKey = `${fromNode} -> ${toNode}`;
                links[linkKey] = (links[linkKey] || 0) + 1;
            }
        });

        const sankeyData = Object.keys(links).map(key => {
            const [from, to] = key.split(' -> ');
            return [from, to, links[key]];
        });

        if (sankeyData.length === 0) {
            return {
                title: { text: 'Applicant Flow Analysis' },
                subtitle: { text: 'No data available for the selected filters.' },
                series: []
            };
        }

        return {
            title: {
                text: 'HR Sankey Analysis'
            },
            subtitle: {
                text: 'Flow of applicants through selected demographic and qualification stages'
            },
            series: [{
                type: 'sankey',
                name: 'Applicant Count',
                keys: ['from', 'to', 'weight'],
                data: sankeyData,
                dataLabels: {
                    style: {
                        fontSize: sankeyData.length > 50 ? '8px' : '10px'
                    }
                }
            }]
        };

    }, [chartData, selectedStages]);

    const handleStageChange = (event) => {
        const { target: { value } } = event;
        setSelectedStages(typeof value === 'string' ? value.split(',') : value);
    };

    const chartOptionsXaxis = useMemo(() => {
        const monthlyData = {};

        // 1. Process the raw data
        chartXData.forEach(item => {
            const date = new Date(item.Action_Effective_Date);
            // Create a key like "July 2023" for grouping
            const month = date.toLocaleString('default', { month: 'long', year: 'numeric' });

            if (!monthlyData[month]) {
                monthlyData[month] = {
                    actions: 0,
                    promotions: 0,
                    increments: 0,
                    males: 0,
                    females: 0
                };
            }

            monthlyData[month].actions++;
            if (item.NatureOfActionTitle === 'PROMOTION') {
                monthlyData[month].promotions++;
            }
            if (item.NatureOfActionTitle === 'SALARY INCREMENT') {
                monthlyData[month].increments++;
            }
            if (item.Sex === 'M') {
                monthlyData[month].males++;
            }
            if (item.Sex === 'F') {
                monthlyData[month].females++;
            }
        });

        // 2. Prepare data for Highcharts series
        const categories = Object.keys(monthlyData);
        const actions = categories.map(month => monthlyData[month].actions);
        const promotions = categories.map(month => monthlyData[month].promotions);
        const increments = categories.map(month => monthlyData[month].increments);
        const males = categories.map(month => monthlyData[month].males);
        const females = categories.map(month => monthlyData[month].females);

        // 3. Return the Highcharts configuration object
        return {
            chart: {
                zoomType: 'xy'
            },
            title: {
                text: 'GG1 Correlation Analysis',
                align: 'left'
            },
            xAxis: [{
                categories: categories,
                crosshair: true
            }],
            yAxis: [{ // Primary yAxis for counts
                labels: {
                    format: '{value}',
                    style: {
                        color: Highcharts.getOptions().colors[0]
                    }
                },
                title: {
                    text: 'Number of Actions',
                    style: {
                        color: Highcharts.getOptions().colors[0]
                    }
                }
            }, { // Secondary yAxis for gender
                title: {
                    text: 'Gender Count',
                    style: {
                        color: Highcharts.getOptions().colors[1]
                    }
                },
                labels: {
                    format: '{value}',
                    style: {
                        color: Highcharts.getOptions().colors[1]
                    }
                },
                opposite: true
            }],
            tooltip: {
                shared: true
            },
            legend: {
                align: 'left',
                verticalAlign: 'top',
                y: 60,
                floating: true,
                backgroundColor: 'rgba(255,255,255,0.25)'
            },
            series: [{
                name: 'Total Actions',
                type: 'column',
                yAxis: 0,
                data: actions,
                tooltip: { valueSuffix: '' }
            }, {
                name: 'Promotions',
                type: 'column',
                yAxis: 0,
                data: promotions,
                tooltip: { valueSuffix: '' }
            }, {
                name: 'Increments',
                type: 'column',
                yAxis: 0,
                data: increments,
                tooltip: { valueSuffix: '' }
            }, {
                name: 'Males',
                type: 'spline',
                yAxis: 1,
                data: males,
                tooltip: { valueSuffix: '' }
            }, {
                name: 'Females',
                type: 'spline',
                yAxis: 1,
                data: females,
                tooltip: { valueSuffix: '' }
            }]
        };
    }, [chartXData]);

    return (
        <Box sx={{ padding: 2 }}>
            <Grid container spacing={0} sx={{ mb: 1 }}>
                {/* --- CORRECTED: Replaced 'size' with 'item xs' --- */}
                <Grid item xs={12} sm={6} md={5}>
                    <Item sx={{ paddingY: 1, paddingX: 0 }}>
                        <TextField
                            label="Search by Announcement ID or Application No. or Pay Grade or Position Title"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleKeyPress}
                            variant="outlined"
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {searchTerm && (
                                            <IconButton
                                                aria-label="clear search"
                                                onClick={handleClearSearch}
                                            >
                                                <ClearIcon />
                                            </IconButton>
                                        )}
                                        <IconButton
                                            aria-label="search"
                                            onClick={handleSearch}
                                        >
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Item>
                </Grid>
                {/* --- CORRECTED: Replaced 'size' with 'item xs' --- */}
                <Grid item xs={12} sm={6} md={5}>
                    <Item >
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                            <TextField
                                label="To Date"
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Box>
                    </Item>
                </Grid>
                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Item sx={{ width: '100%' }}>
                        <Button variant="outlined" size='large' style={{ padding: '5px' }} onClick={handleReset} fullWidth>
                            Reset All
                        </Button>
                    </Item>
                </Grid>
            </Grid>

            <FormControl sx={{ my: 2, width: '100%' }}>
                <InputLabel id="stage-select-label">Select Stages to Display</InputLabel>
                <Select
                    labelId="stage-select-label"
                    multiple
                    value={selectedStages}
                    onChange={handleStageChange}
                    input={
                        <OutlinedInput
                            label="Select Stages to Display"
                            endAdornment={
                                selectedStages.length > 0 && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="clear selection"
                                            onClick={() => setSelectedStages([])}
                                            edge="end"
                                            style={{ marginRight: 5 }}
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        />
                    }
                    renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                                <Chip key={value} label={value} />
                            ))}
                        </Box>
                    )}
                >
                    {allPossibleStages.map((stage) => (
                        <MenuItem key={stage} value={stage}>
                            {stage}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <HighchartsReact
                highcharts={Highcharts}
                options={chartOptions}
            />

            {/* <HighchartsReact
                highcharts={Highcharts}
                options={chartOptionsXaxis}
            /> */}
        </Box>
    );
};

export default JobShankeyChart;